import React, { useEffect, useLayoutEffect } from 'react'
import { Dialog, DialogActions, DialogContent, Grid, Paper, Typography, Button } from '@mui/material'
import { CANCLE_ORDER_DIALOG_ID, ORDER_DETAIL_DIALOG_ID, ORDER_EDIT_DIALOG_ID } from '../../constants/dialogConstants'
import { useDispatch } from 'react-redux'
import { closeDialog, openDialog } from '../../redux/actions/dialogActions'
import { useSelector } from '../../redux/hooks/useSelector'
import { InstrumentIcon } from '../common/InstrumentIcon'
import { useInstrumentIconType } from '../../hooks/useInstrumentIconType'
import manS from '../../assets/manS.jpg'
import { FillTable } from '../OrderDetailDialog.tsx/FillTable'
import { useClientApi } from '../../hooks/useClientApi'
import { useInstrumentApi } from '../../hooks/useInstrumentApi'
import NumberFormat from 'react-number-format'
import { useInputDialogColor } from '../../hooks/useInputDialogColor'
import { emptyClient, newOrder } from '../../constants/initialStates'
import { OrderAction } from '../../redux/reducers/orders'
import { getClientAction } from '../../redux/actions/clientActions'
import { getOrderAction, getSecurityAction } from '../../redux/actions/instrumentActions'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import {
  ORDER_STATE_CONFIRMED,
  ORDER_STATE_NEW,
  ORDER_STATE_PARTIAL_FILLED,
  ORDER_TYPE_AMOUNT,
  ORDER_TYPE_QUANTITY,
} from '../../constants/codeLists'
import makeStyles from '@mui/styles/makeStyles'
import { CancleOrderDialog } from './CancleOrderDialog'
import { tokens } from '../../styles/tokens'
import useMobileWidth from '../../hooks/useMobileWidth'

const useStyles = makeStyles(theme => ({
  dialogWindow: {},
  DialogHeader: {
    width: '100%',
    height: 60,
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.primary.contrastText,
    paddingLeft: theme.spacing(2),
    paddingBottom: theme.spacing(0.75),
    paddingRight: theme.spacing(2),
    justifyContent: 'center',
  },
  dialogContentContainer: {
    display: 'flex',
    flexDirection: 'row',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  cardsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    width: '550px',
    maxWidth: '100%',
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  card: {
    borderRadius: tokens.radius.lg,
    boxShadow: tokens.shadow.sm,
    padding: theme.spacing(2),
  },
  commodityContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  topContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconSize: {
    height: '25px',
    width: '45px',
    lineHeight: '25px',
    fontSize: '12px',
  },
  topLineText: {
    marginLeft: theme.spacing(2),
  },
  boldText: {
    fontWeight: 'bold',
  },
  textColor: {
    color: tokens.color.primary[700],
  },
  topLineSeparator: {
    flexGrow: 1,
  },
  bottomContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  securityInfo: {
    marginRight: theme.spacing(1),
    color: tokens.color.primary[700],
  },
  percentage: {
    color: theme.palette.success.main,
  },
  clientContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  clientPersonalInfoContainer: {
    color: tokens.color.primary[700],
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  clientIcon: {
    height: '40px',
    width: '40px',
    borderRadius: tokens.radius.full,
  },
  clientCredentials: {
    marginLeft: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
  },
  clientPortfolioContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    color: tokens.color.primary[700],
  },
  actionButtons: {
    width: '15%',
  },
  cancelButton: {
    backgroundColor: theme.palette.error.main,
  },
  editButton: {
    backgroundColor: theme.palette.warning.main,
  },
  detailRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 0),
  },
  label: {
    color: tokens.color.neutral[500],
    fontSize: '0.875rem',
  },
  value: {
    color: tokens.color.neutral[900],
    fontWeight: 600,
    fontSize: '0.875rem',
    textAlign: 'right',
  },
  isin: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
}))

export const OrderDetailDialog = () => {
  const dispatch = useDispatch()
  const { getClient } = useClientApi()
  const { getSecurity, getOrder } = useInstrumentApi()
  const classes = useStyles()
  const isMobile = useMobileWidth()
  const dialogs = useSelector(state => state.dialogs.dialogs)
  const foundDialog = dialogs.find(dialog => dialog.id === 'ORDER_DETAIL_DIALOG_ID')
  const open = !!foundDialog
  const security = useSelector(state => state.securities.security)
  const instrumentIconType = useInstrumentIconType(!security ? null : security?.assetClass)
  const client = useSelector(state => state.clients.client)
  const order = useSelector(state => state.orders.order)
  const getInputDialogColor = useInputDialogColor()
  const { t } = useTranslation()

  useEffect(() => {
    if (foundDialog) {
      getClient(foundDialog?.payload.clientId)
      getSecurity(foundDialog?.payload.securityId)
      getOrder(foundDialog?.payload.orderId)
    }
  }, [foundDialog])

  useLayoutEffect(() => {
    return () => {
      dispatch(closeDialog(ORDER_DETAIL_DIALOG_ID))
    }
  }, [])

  const handleClose = () => {
    dispatch(closeDialog(ORDER_DETAIL_DIALOG_ID))
  }

  const handleEdit = () => {
    dispatch(openDialog(ORDER_EDIT_DIALOG_ID))
    dispatch(closeDialog(ORDER_DETAIL_DIALOG_ID))
  }

  const handleDelete = () => {
    dispatch(openDialog(CANCLE_ORDER_DIALOG_ID))
  }

  const isEditDisabled = () => {
    // Editable and cancelable states are new, confirmed and partially filled
    return (
      order.state !== ORDER_STATE_NEW &&
      order.state !== ORDER_STATE_CONFIRMED &&
      order.state !== ORDER_STATE_PARTIAL_FILLED
    )
  }

  const partsFilled = order.actions
    .map((e: OrderAction) => (e.quantity ? e.quantity : 0))
    .reduce((prev, curr) => prev + curr, 0)

  if (!security) return null

  return (
    <div>
      <Dialog
        maxWidth="xl"
        fullWidth={isMobile}
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        className={`${classes.dialogWindow} `}
      >
        <div className={`${classes.DialogHeader} ${getInputDialogColor(order.direction, false)}`}>
          <Typography variant="h5">{t('ORDER_DETAIL') + ' ' + order.orderIdent}</Typography>
        </div>
        <DialogContent>
          <div className={classes.dialogContentContainer}>
            <div>
              <FillTable />
            </div>
            <div className={classes.cardsColumn}>
              <Paper className={`${classes.card} ${classes.commodityContainer}`}>
                <div className={classes.topContainer}>
                  <InstrumentIcon
                    className={`${classes.iconSize} ${instrumentIconType}`}
                    securityType={security.assetClass}
                  />
                  <span className={`${classes.topLineText} ${classes.boldText} ${classes.textColor}`}>
                    {security.name}
                  </span>
                  <div className={classes.topLineSeparator} />
                  <div className={classes.isin}>
                    <span className={` ${classes.boldText} ${classes.textColor}`}>{security.isin}</span>
                    <span className={` ${classes.textColor}`}>{t(security.instrumentType)}</span>
                  </div>
                </div>
                <div className={classes.bottomContainer}>
                  <span className={classes.securityInfo}>
                    {format(new Date(security.lastQuoteDate), 'dd/MM/yyyy')}{' '}
                    <span className={classes.boldText}>
                      <NumberFormat
                        value={security.lastQuotePrice}
                        displayType="text"
                        thousandSeparator=" "
                        decimalScale={8}
                        decimalSeparator=","
                      />
                    </span>{' '}
                    {security.currency} <span className={classes.percentage}>{security.lastPriceIndication}</span>
                  </span>
                  <div className={classes.topLineSeparator} />
                  <span className={classes.securityInfo}>{security.market}</span>
                </div>
              </Paper>
              <Paper className={`${classes.card} ${classes.clientContainer}`}>
                <div className={classes.clientPersonalInfoContainer}>
                  <img alt="" className={classes.clientIcon} src={manS} width={20} />
                  <div className={classes.clientCredentials}>
                    <span>
                      {client?.company && <>{client.companyName}</>}
                      {!client?.company && <>{client.firstName} <span className={classes.boldText}>{client.surname}</span></>}
                    </span>
                    <span>
                      <NumberFormat value={client.telephoneNumber} displayType={'text'} format="### ### ### ###" />
                    </span>
                  </div>
                </div>
                <div className={classes.clientPortfolioContainer}></div>
              </Paper>
              <Paper className={classes.card}>
                <Grid container spacing={0} columnSpacing={3}>
                  <Grid item xs={12} sm={6} className={classes.detailRow}>
                    <Typography className={classes.label}>{t('MARKET_TYPE')}</Typography>
                    <Typography className={classes.value}>{t(order.type)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} className={classes.detailRow}>
                    <Typography className={classes.label}>{t('QUANTITY')}</Typography>
                    <Typography className={classes.value}>
                      {order.type === ORDER_TYPE_AMOUNT && (
                        <NumberFormat
                          value={order.amount}
                          displayType="text"
                          thousandSeparator=" "
                          fixedDecimalScale
                          decimalScale={2}
                          decimalSeparator=","
                        />
                      )}
                      {order.type === ORDER_TYPE_QUANTITY && (
                        <>
                          <NumberFormat
                            value={order.quantity}
                            displayType="text"
                            thousandSeparator=" "
                            decimalScale={4}
                            decimalSeparator=","
                          />
                          {' ' + t('UNIT')}
                        </>
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} className={classes.detailRow}>
                    <Typography className={classes.label}>{t('CRETATION_DATE')}</Typography>
                    <Typography className={classes.value}>
                      {order.orderCreationDate ? format(new Date(order.orderCreationDate), 'dd/MM/yyyy HH:mm') : ''}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} className={classes.detailRow}>
                    <Typography className={classes.label}>{t('ISSUE_DATE')}</Typography>
                    <Typography className={classes.value}>
                      {order.acceptationDate ? format(new Date(order.acceptationDate), 'dd/MM/yyyy HH:mm') : ''}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6} className={classes.detailRow}>
                    <Typography className={classes.label}>{t('DEAL_CURRENCY')}</Typography>
                    <Typography className={classes.value}>{order.currency}</Typography>
                  </Grid>

                  <Grid item xs={12} sm={6} className={classes.detailRow}>
                    <Typography className={classes.label}>{t('VALIDITY_TYPE')}</Typography>
                    <Typography className={classes.value}>{t(order.validityType)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} className={classes.detailRow}>
                    <Typography className={classes.label}>{t('ORDER_DIRECTION2')}</Typography>
                    <Typography className={classes.value}>{t(order.direction)}</Typography>
                  </Grid>
                  {order.validityType === 'OV_GTD' && (
                    <Grid item xs={12} sm={6} className={classes.detailRow}>
                      <Typography className={classes.label}>{t('VALIDITY_TO2')}</Typography>
                      <Typography className={classes.value}>
                        {order.validityDate ? format(new Date(order.validityDate), 'dd/MM/yyyy') : ''}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
              <Paper className={classes.card}>
                <Grid container spacing={0}>
                  <Grid item xs={12} className={classes.detailRow}>
                    <Typography className={classes.label}>{t('ID_TAKING_ORDER')}</Typography>
                    <Typography className={classes.value}>{order.omsId}</Typography>
                  </Grid>
                  <Grid item xs={12} className={classes.detailRow}>
                    <Typography className={classes.label}>{t('ORDER_STATE2')}</Typography>
                    <Typography className={classes.value}>{order.state && t(order.state)}</Typography>
                  </Grid>
                  <Grid item xs={12} className={classes.detailRow}>
                    <Typography className={classes.label}>{t('FIX_DESCRIPTION')}</Typography>
                    <Typography className={classes.value}>{order.errorDescription}</Typography>
                  </Grid>
                  <Grid item xs={12} className={classes.detailRow}>
                    <Typography className={classes.label}>{t('REALIZED')}</Typography>
                    <Typography className={classes.value}>{order.filledQuantity} ks</Typography>
                  </Grid>
                  <Grid item xs={12} className={classes.detailRow}>
                    <Typography className={classes.label}>{t('REMAINS_REALIZED')}</Typography>
                    <Typography className={classes.value}>
                      {(order.quantity || 0) - (order.filledQuantity || 0)} ks
                    </Typography>
                  </Grid>
                  <Grid item xs={12} className={classes.detailRow}>
                    <Typography className={classes.label}>{t('FEES')}</Typography>
                    <Typography className={classes.value}>
                      <NumberFormat
                        value={order.fee}
                        displayType="text"
                        thousandSeparator=" "
                        fixedDecimalScale
                        decimalScale={2}
                        decimalSeparator=","
                      />{' '}
                      {order.currency}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            className={`${classes.actionButtons} ${classes.cancelButton}`}
            disabled={isEditDisabled()}
            size="large"
            variant="contained"
            color="primary"
            onClick={handleDelete}
          >
            {t('CANCLE_ORDER')}
          </Button>
          <Button
            className={classes.actionButtons}
            size="large"
            variant="contained"
            color="primary"
            onClick={handleClose}
          >
            {t('DIALOG_CLOSE')}
          </Button>
        </DialogActions>
        <CancleOrderDialog order={order} />
      </Dialog>
    </div>
  )
}
